import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { LEGAL_DOCUMENTS, MARKETING_CONSENT_ENABLED } from "@/lib/legalDocuments";

/**
 * Required consent controls presented immediately above the "Submit Application"
 * button on Step 9. Each checkbox is:
 *   - unchecked by default (see initialState in applyForm.js)
 *   - keyboard accessible
 *   - not bundled — each represents a separate consent
 *   - includes a persistent link to the relevant legal document
 *
 * The optional marketing checkbox (#6) is only rendered when
 * MARKETING_CONSENT_ENABLED is true. Its state is stored on the data object
 * but never blocks form submission when unchecked.
 */

const OpenInNewTab = ({ to, children, testId }) => (
  <Link
    to={to}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="underline text-[#5A3B31] hover:text-[#2A1C14]"
  >
    {children}
  </Link>
);

const consentItems = [
  {
    key: "consent_terms",
    doc: LEGAL_DOCUMENTS.terms,
    required: true,
    render: () => (
      <>
        I have read and agree to the{" "}
        <OpenInNewTab to="/terms" testId="consent-link-terms">Terms of Use</OpenInNewTab>.
      </>
    ),
  },
  {
    key: "consent_privacy",
    doc: LEGAL_DOCUMENTS.privacy,
    required: true,
    render: () => (
      <>
        I acknowledge that I have read the{" "}
        <OpenInNewTab to="/privacy" testId="consent-link-privacy">Privacy Policy</OpenInNewTab>{" "}
        and understand how Hannah Howard Funding collects, uses, discloses, and retains information submitted through this application.
      </>
    ),
  },
  {
    key: "consent_authorization",
    doc: LEGAL_DOCUMENTS.authorization,
    required: true,
    render: () => (
      <>
        I authorize Hannah Howard Funding to verify the information and documents in my application and to communicate with my brokerage, managing broker, title company, settlement or escrow company, closing attorney, transaction coordinator, lender, or other authorized transaction participants as described in the{" "}
        <OpenInNewTab to="/application-authorization" testId="consent-link-authorization">Application Authorization</OpenInNewTab>.
      </>
    ),
  },
  {
    key: "consent_esign",
    doc: LEGAL_DOCUMENTS.esign,
    required: true,
    render: () => (
      <>
        I consent to receive records, disclosures, agreements, and notices electronically and to use electronic signatures as described in the{" "}
        <OpenInNewTab to="/esign-consent" testId="consent-link-esign">E-SIGN Consent</OpenInNewTab>.
      </>
    ),
  },
  {
    key: "consent_communications",
    doc: LEGAL_DOCUMENTS.tcpa,
    required: true,
    render: () => (
      <>
        I agree that Hannah Howard Funding and its service providers may contact me by telephone, email, and SMS regarding my inquiry, application, document requests, verification, scheduling, funding, servicing, security, and related transactional communications. Message and data rates may apply. Message frequency varies. Reply STOP to opt out of texts and HELP for help. See{" "}
        <OpenInNewTab to="/communications-consent" testId="consent-link-communications">Communications Consent</OpenInNewTab>.
      </>
    ),
  },
];

const marketingItem = {
  key: "consent_marketing",
  doc: LEGAL_DOCUMENTS.tcpa,
  required: false,
  render: () => (
    <>
      I agree to receive recurring promotional calls, emails, and text messages from Hannah Howard Funding at the contact information I provided, including messages sent using automated technology where applicable. Consent is not a condition of applying for or receiving funding. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe and HELP for help. See{" "}
      <OpenInNewTab to="/communications-consent" testId="consent-link-marketing">Communications Consent</OpenInNewTab>.
    </>
  ),
};

function ConsentRow({ item, data, patch, showError }) {
  const checked = !!data[item.key];
  const missing = showError && item.required && !checked;
  return (
    <label
      className={`flex items-start gap-3 p-5 rounded-2xl border cursor-pointer transition-colors ${
        missing ? "border-red-400 bg-red-50/60" : "border-[#E8DFD8] hover:border-[#CBA4A4]"
      }`}
    >
      <Checkbox
        data-testid={`consent-${item.key}`}
        checked={checked}
        onCheckedChange={(v) => patch({ [item.key]: !!v })}
        aria-required={item.required}
        aria-invalid={missing ? "true" : "false"}
      />
      <div className="flex-1">
        <span className="text-sm text-[#2A1C14] leading-relaxed block">
          {item.render()}
        </span>
        {item.required ? (
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#8C7A70] mt-1 inline-block">
            Required
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#8C7A70] mt-1 inline-block">
            Optional
          </span>
        )}
        {missing ? (
          <p
            className="text-xs text-red-600 mt-1"
            role="alert"
            data-testid={`consent-error-${item.key}`}
          >
            This consent is required to submit your application.
          </p>
        ) : null}
      </div>
    </label>
  );
}

export default function ConsentControls({ data, patch, showError }) {
  return (
    <div className="space-y-4" data-testid="consent-controls">
      <p className="text-sm text-[#5A3B31] leading-relaxed">
        Before submitting your application, please review and acknowledge each of the following. Each consent below is separate — please read them carefully.
      </p>
      {consentItems.map((item) => (
        <ConsentRow
          key={item.key}
          item={item}
          data={data}
          patch={patch}
          showError={showError}
        />
      ))}
      {MARKETING_CONSENT_ENABLED ? (
        <ConsentRow
          key={marketingItem.key}
          item={marketingItem}
          data={data}
          patch={patch}
          showError={showError}
        />
      ) : null}
    </div>
  );
}

export const REQUIRED_CONSENT_KEYS = consentItems
  .filter((i) => i.required)
  .map((i) => i.key);

export const CONSENT_ITEM_LABELS = Object.fromEntries(
  [...consentItems, marketingItem].map((item) => {
    // Extract text content from the render function for audit logging.
    // Since render() returns JSX, we build a plain-text label separately for the audit trail.
    return [item.key, item];
  })
);

// Plain-text labels used for the audit record's "exact checkbox wording shown"
// field. These MUST match the visible text — update in lockstep with render()
// functions above whenever legal copy changes.
export const CONSENT_PLAIN_TEXT = {
  consent_terms:
    "I have read and agree to the Terms of Use.",
  consent_privacy:
    "I acknowledge that I have read the Privacy Policy and understand how Hannah Howard Funding collects, uses, discloses, and retains information submitted through this application.",
  consent_authorization:
    "I authorize Hannah Howard Funding to verify the information and documents in my application and to communicate with my brokerage, managing broker, title company, settlement or escrow company, closing attorney, transaction coordinator, lender, or other authorized transaction participants as described in the Application Authorization.",
  consent_esign:
    "I consent to receive records, disclosures, agreements, and notices electronically and to use electronic signatures as described in the E-SIGN Consent.",
  consent_communications:
    "I agree that Hannah Howard Funding and its service providers may contact me by telephone, email, and SMS regarding my inquiry, application, document requests, verification, scheduling, funding, servicing, security, and related transactional communications. Message and data rates may apply. Message frequency varies. Reply STOP to opt out of texts and HELP for help.",
  consent_marketing:
    "I agree to receive recurring promotional calls, emails, and text messages from Hannah Howard Funding at the contact information I provided, including messages sent using automated technology where applicable. Consent is not a condition of applying for or receiving funding. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe and HELP for help.",
};
